import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Package, Boxes, Warehouse, TrendingUp, Search, Filter, 
  Plus, RefreshCw, ArrowUpDown, ChevronDown, ChevronRight, 
  AlertTriangle, CheckCircle, Clock, ArrowLeft, Activity
} from 'lucide-react';
import { useNavigation } from '../contexts/NavigationContext';
import { useTeachingMode } from '../contexts/TeachingModeContext';
import EnhancedTeachingAids from './EnhancedTeachingAids';

const InventoryView = ({ navigationSource = null }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [inventoryStatus, setInventoryStatus] = useState([]);
  const [inventoryTransactions, setInventoryTransactions] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryAnalytics, setInventoryAnalytics] = useState({});
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Navigation context
  const { navigateTo } = useNavigation();
  const { showTeachingAids } = useTeachingMode();

  // Fetch data from Supabase
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*');

      if (productsError) throw productsError;

      // Fetch warehouses
      const { data: warehousesData, error: warehousesError } = await supabase
        .from('warehouses')
        .select('*');

      if (warehousesError) throw warehousesError;

      // Fetch inventory status
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory_items')
        .select('*');

      if (inventoryError) throw inventoryError;

      // Fetch inventory movements
      const { data: movementsData, error: movementsError } = await supabase
        .from('inventory_movements')
        .select('*')
        .order('movement_date', { ascending: false });

      if (movementsError) throw movementsError;

      // Fetch suppliers
      const { data: suppliersData, error: suppliersError } = await supabase
        .from('suppliers')
        .select('*');

      if (suppliersError) throw suppliersError;

      setProducts(productsData || []);
      setWarehouses(warehousesData || []);
      setInventoryStatus(inventoryData || []);
      setInventoryTransactions(movementsData || []);
      setSuppliers(suppliersData || []);

      // Define product categories
      setProductCategories([
        { id: 'Industrial Equipment', name: '工業設備' },
        { id: 'Electronic Components', name: '電子元件' },
        { id: 'Raw Materials', name: '原材料' },
        { id: 'Finished Goods', name: '成品' },
        { id: 'Packaging Materials', name: '包裝材料' },
        { id: 'Semi-finished Products', name: '半成品' },
        { id: 'Spare Parts', name: '零配件' }
      ]);

      // Define transaction types
      setTransactionTypes([
        { id: 'receipt', name: '收貨', color: '#10B981' },
        { id: 'issue', name: '發貨', color: '#EF4444' },
        { id: 'transfer', name: '調撥', color: '#3B82F6' },
        { id: 'adjustment', name: '調整', color: '#F59E0B' },
        { id: 'count', name: '盤點', color: '#8B5CF6' }
      ]);

      // Calculate inventory analytics from real data
      const totalInventoryValue = inventoryData?.reduce((sum, item) => {
        return sum + ((parseFloat(item.current_stock) || 0) * (parseFloat(item.standard_cost) || 0));
      }, 0) || 0;

      // Calculate category breakdown
      const categoryBreakdown = {};
      inventoryData?.forEach(item => {
        const category = item.category || '其他';
        const value = (parseFloat(item.current_stock) || 0) * (parseFloat(item.standard_cost) || 0);
        if (!categoryBreakdown[category]) {
          categoryBreakdown[category] = { category, value: 0, count: 0 };
        }
        categoryBreakdown[category].value += value;
        categoryBreakdown[category].count += 1;
      });

      // Convert to array and calculate percentages
      const categoryBreakdownArray = Object.values(categoryBreakdown).map(cat => ({
        category: cat.category,
        value: cat.value,
        percentage: totalInventoryValue > 0 ? (cat.value / totalInventoryValue) * 100 : 0
      }));

      // Calculate warehouse utilization
      const warehouseUtilization = warehousesData?.map(warehouse => ({
        warehouse_id: warehouse.warehouse_id,
        warehouse_name: warehouse.warehouse_name,
        utilization: warehouse.capacity > 0 ? (warehouse.current_utilization / warehouse.capacity) * 100 : 0,
        current: warehouse.current_utilization,
        capacity: warehouse.capacity
      })) || [];

      // Calculate reorder alerts
      const reorderAlerts = inventoryData?.filter(item => {
        const currentStock = parseFloat(item.current_stock) || 0;
        const reorderPoint = parseFloat(item.reorder_point) || 0;
        return currentStock <= reorderPoint;
      }).map(item => ({
        product_id: item.item_id,
        product_name: item.item_name,
        current_stock: item.current_stock,
        reorder_point: item.reorder_point,
        status: item.current_stock === 0 ? 'out_of_stock' : 'low_stock'
      })) || [];

      const analytics = {
        total_products: productsData?.length || 0,
        total_warehouses: warehousesData?.length || 0,
        total_transactions: movementsData?.length || 0,
        low_stock_items: inventoryData?.filter(item => (parseFloat(item.current_stock) || 0) <= (parseFloat(item.reorder_point) || 0)).length || 0,
        out_of_stock_items: inventoryData?.filter(item => (parseFloat(item.current_stock) || 0) === 0).length || 0,
        total_inventory_value: totalInventoryValue,
        inventory_turnover: 4.2, // Simplified calculation
        average_days_to_sell: 87, // Simplified calculation
        stockout_rate: 0.5, // Simplified calculation
        category_breakdown: categoryBreakdownArray,
        warehouse_utilization: warehouseUtilization,
        reorder_alerts: reorderAlerts
      };

      setInventoryAnalytics(analytics);
      console.log('Inventory analytics set:', analytics);

    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle return to workflows
  const handleReturnToWorkflows = () => {
    navigateTo('workflows');
  };
  
  // 庫存狀態計算
  const getProductStock = (productId, warehouseId = 'all') => {
    return inventoryStatus
      .filter(item => item.item_id === productId && (warehouseId === 'all' || item.location_id === warehouseId))
      .reduce((total, item) => total + (parseFloat(item.current_stock) || 0), 0);
  };

  // 產品庫存狀態判斷
  const getStockStatus = (product) => {
    const currentStock = getProductStock(product.product_id);
    if (currentStock <= (parseFloat(product.reorder_point) || 0)) return 'critical';
    if (currentStock <= (parseFloat(product.reorder_point) * 1.5 || 0)) return 'warning';
    return 'normal';
  };

  // 篩選產品
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         product.product_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // 檢查是否有庫存在選定的倉庫
    const hasStockInWarehouse = selectedWarehouse === 'all' || 
      inventoryStatus.some(status => 
        status.item_id === product.product_id && 
        status.location_id === selectedWarehouse && 
        (parseFloat(status.current_stock) || 0) > 0
      );
    
    return matchesCategory && matchesSearch && (selectedWarehouse === 'all' || hasStockInWarehouse);
  });

  // 排序產品
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let valueA, valueB;
    
    switch (sortField) {
      case 'name':
        valueA = a.product_name || '';
        valueB = b.product_name || '';
        break;
      case 'sku':
        valueA = a.product_code || '';
        valueB = b.product_code || '';
        break;
      case 'category':
        valueA = productCategories.find(cat => cat.id === a.category)?.name || '';
        valueB = productCategories.find(cat => cat.id === b.category)?.name || '';
        break;
      case 'stock':
        valueA = getProductStock(a.product_id, selectedWarehouse);
        valueB = getProductStock(b.product_id, selectedWarehouse);
        break;
      case 'price':
        valueA = parseFloat(a.base_price) || 0;
        valueB = parseFloat(b.base_price) || 0;
        break;
      default:
        valueA = a.product_name || '';
        valueB = b.product_name || '';
    }
    
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // 取得產品類別名稱
  const getCategoryName = (categoryId) => {
    const category = productCategories.find(cat => cat.id === categoryId);
    return category ? category.name : '未分類';
  };

  // 取得供應商名稱
  const getSupplierName = (supplierId) => {
    return suppliers.find(sup => sup.id === supplierId)?.name || '自製';
  };

  // 取得倉庫名稱
  const getWarehouseName = (warehouseId) => {
    return warehouses.find(wh => wh.id === warehouseId)?.name || '未知倉庫';
  };

  // 取得交易類型名稱
  const getTransactionTypeName = (typeId) => {
    return transactionTypes.find(type => type.id === typeId)?.name || '未知類型';
  };

  // 取得產品相關交易
  const getProductTransactions = (productId) => {
    return inventoryTransactions
      .filter(trans => trans.product_id === productId)
      .sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));
  };

  // 排序處理
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // 渲染庫存儀表板
  const renderDashboard = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading inventory data...</div>
        </div>
      );
    }

    const { 
      total_inventory_value = 0, 
      inventory_turnover = 0, 
      average_days_to_sell = 0, 
      stockout_rate = 0, 
      category_breakdown = [], 
      warehouse_utilization = [], 
      reorder_alerts = [] 
    } = inventoryAnalytics || {};

    return (
      <div className="space-y-6">
        {/* 庫存概況卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">總庫存價值</p>
                <h3 className="text-2xl font-bold">HK${(total_inventory_value || 0).toLocaleString()}</h3>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-xs text-green-500">較上月增加 2.9%</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">庫存周轉率</p>
                <h3 className="text-2xl font-bold">{inventory_turnover || 0} 次/年</h3>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <RefreshCw className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-xs text-gray-500">平均 {average_days_to_sell || 0} 天售出</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">缺貨率</p>
                <h3 className="text-2xl font-bold">{stockout_rate || 0}%</h3>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-xs text-gray-500">低於行業平均 1.2%</span>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">倉庫利用率</p>
                <h3 className="text-2xl font-bold">
                  {warehouse_utilization && warehouse_utilization.length > 0 
                    ? Math.round(warehouse_utilization.reduce((sum, wh) => sum + (wh.utilization || 0), 0) / warehouse_utilization.length)
                    : 0}%
                </h3>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Warehouse className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <span className="text-xs text-gray-500">3 個倉庫</span>
            </div>
          </div>
        </div>

        {/* 庫存分類與倉庫利用率 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-medium mb-4">庫存類別分佈</h3>
            <div className="space-y-3">
              {category_breakdown.map(cat => (
                <div key={cat.category} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>{getCategoryName(cat.category)}</span>
                    <span className="font-medium">HK${cat.value.toLocaleString()} ({cat.percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full" 
                      style={{
                        width: `${cat.percentage}%`,
                        backgroundColor: productCategories.find(c => c.id === cat.category)?.color || '#CBD5E0'
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-md">
            <h3 className="text-lg font-medium mb-4">倉庫利用率</h3>
            <div className="space-y-4">
              {warehouse_utilization.map(wh => {
                const warehouse = warehouses.find(w => w.warehouse_id === wh.warehouse_id);
                let barColor = 'bg-green-500';
                if (wh.utilization > 85) barColor = 'bg-red-500';
                else if (wh.utilization > 70) barColor = 'bg-yellow-500';

                return (
                  <div key={wh.warehouse_id} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{wh.warehouse_name || '未知倉庫'}</span>
                      <span className="font-medium">{Math.round(wh.utilization)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${barColor}`}
                        style={{ width: `${wh.utilization}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {wh.current?.toLocaleString()} / {wh.capacity?.toLocaleString()} 單位
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 補貨警示 */}
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h3 className="text-lg font-medium mb-4">補貨警示</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">目前庫存</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">安全庫存</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">補貨點</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">供應商</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">前置時間</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reorder_alerts.map(alert => {
                  const product = products.find(p => p.product_id === alert.product_id);
                  if (!product) return null;
                  
                  let statusColor = 'text-green-600 bg-green-100';
                  if (alert.status === 'out_of_stock') statusColor = 'text-red-600 bg-red-100';
                  else if (alert.status === 'low_stock') statusColor = 'text-yellow-600 bg-yellow-100';
                  
                  return (
                    <tr key={alert.product_id}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 mr-3 bg-gray-200 rounded-md flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{alert.product_name}</div>
                            <div className="text-xs text-gray-500">{product?.category || '未知類別'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{product?.product_code || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">{alert.current_stock}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{product?.safety_stock || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{alert.reorder_point}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColor}`}>
                          {alert.status === 'out_of_stock' ? '缺貨' : alert.status === 'low_stock' ? '庫存不足' : '庫存充足'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{product?.supplier_name || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{product.lead_time} 天</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // 渲染產品列表
  const renderProducts = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading products...</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* 過濾與搜尋 */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="搜尋產品名稱或SKU..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            <div className="relative">
              <select
                className="appearance-none pl-10 pr-8 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">所有類別</option>
                {productCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <ChevronDown className="absolute right-2 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            
            <div className="relative">
              <select
                className="appearance-none pl-10 pr-8 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
              >
                <option value="all">所有倉庫</option>
                {warehouses.map(wh => (
                  <option key={wh.warehouse_id} value={wh.warehouse_id}>{wh.warehouse_name}</option>
                ))}
              </select>
              <Warehouse className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <ChevronDown className="absolute right-2 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          
          <button className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="h-5 w-5" />
            <span>新增產品</span>
          </button>
        </div>
        
        {/* 產品表格 */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      <span>產品名稱</span>
                      {sortField === 'name' && (
                        <ArrowUpDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('sku')}
                  >
                    <div className="flex items-center">
                      <span>SKU</span>
                      {sortField === 'sku' && (
                        <ArrowUpDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center">
                      <span>類別</span>
                      {sortField === 'category' && (
                        <ArrowUpDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('stock')}
                  >
                    <div className="flex items-center">
                      <span>庫存</span>
                      {sortField === 'stock' && (
                        <ArrowUpDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('price')}
                  >
                    <div className="flex items-center">
                      <span>單價</span>
                      {sortField === 'price' && (
                        <ArrowUpDown className="ml-1 h-4 w-4" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">供應商</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedProducts.map(product => {
                  const isExpanded = expandedProduct === product.id;
                  const stockStatus = getStockStatus(product);
                  const currentStock = getProductStock(product.product_id, selectedWarehouse);
                  
                  let statusBadge = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">庫存充足</span>;
                  if (stockStatus === 'critical') {
                    statusBadge = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">庫存不足</span>;
                  } else if (stockStatus === 'warning') {
                    statusBadge = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">庫存警告</span>;
                  }
                  
                  return (
                    <React.Fragment key={product.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedProduct(isExpanded ? null : product.id)}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            {isExpanded ? 
                              <ChevronDown className="h-4 w-4 mr-2 text-gray-500" /> : 
                              <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                            }
                            <div className="h-10 w-10 flex-shrink-0 mr-3">
                              <img className="h-10 w-10 rounded-md object-cover" src={product.image} alt={product.name} />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{product.name}</div>
                              <div className="text-xs text-gray-500">{product.product_code}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{product.product_code}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2" 
                              style={{ backgroundColor: productCategories.find(cat => cat.id === product.category)?.color || '#CBD5E0' }}
                            ></div>
                            <span className="text-sm text-gray-900">{getCategoryName(product.category)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{currentStock} {product.unit_of_measure || '個'}</div>
                          <div className="text-xs text-gray-500">
                            安全庫存: {product.stock_level || 0} / 補貨點: {product.reorder_point || 0}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          HK${(product.base_price || 0).toFixed(2)}/{product.unit_of_measure || '個'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {statusBadge}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {getSupplierName(product.supplier_id)}
                        </td>
                      </tr>
                      
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">產品詳情</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <p className="text-xs text-gray-500">描述</p>
                                    <p className="text-sm">{product.description}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">前置時間</p>
                                    <p className="text-sm">{product.lead_time} 天</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">總庫存價值</p>
                                    <p className="text-sm font-medium">HK${(currentStock * (product.base_price || 0)).toFixed(2)}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // 渲染倉庫管理
  const renderWarehouses = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading warehouses...</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">倉庫管理</h3>
          <button className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="h-5 w-5" />
            <span>新增倉庫</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {warehouses.map(warehouse => {
            const utilization = inventoryAnalytics.warehouse_utilization.find(wh => wh.warehouse_id === warehouse.warehouse_id)?.utilization || 0;
            let utilizationColor = 'bg-green-500';
            if (utilization > 85) utilizationColor = 'bg-red-500';
            else if (utilization > 70) utilizationColor = 'bg-yellow-500';

            return (
              <div key={warehouse.warehouse_id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-40 w-full relative">
                  <div className="h-full w-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <Warehouse className="h-16 w-16 text-white" />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end">
                    <div className="p-4 text-white">
                      <h4 className="text-lg font-medium">{warehouse.warehouse_name}</h4>
                      <p className="text-sm">{warehouse.location}</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">倉庫管理員</span>
                    <span className="text-sm font-medium">{warehouse.manager}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">容量使用率</span>
                    <span className="text-sm font-medium">{utilization}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${utilizationColor}`}
                      style={{ width: `${utilization}%` }}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {(warehouse.current_utilization || 0).toLocaleString()} / {(warehouse.capacity || 0).toLocaleString()} 單位
                  </div>
                  <div className="flex justify-end pt-2">
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">查看詳情</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 渲染交易記錄
  const renderTransactions = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading transactions...</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium">庫存交易記錄</h3>
          <button className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="h-5 w-5" />
            <span>新增交易</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日期</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">交易類型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">產品</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">倉庫</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">數量</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">單價</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">總金額</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">參考單號</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {inventoryTransactions.map(transaction => {
                  const product = products.find(p => p.product_id === transaction.item_id);
                  const transType = transactionTypes.find(t => t.id === transaction.movement_type);
                  const effectColor = transType?.color || 'text-gray-600'; // Default color
                  
                  return (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transaction.movement_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${effectColor === 'text-green-600' ? 'bg-green-100' : 'bg-red-100'} ${effectColor}`}>
                          {getTransactionTypeName(transaction.movement_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-8 w-8 flex-shrink-0 mr-2">
                            <img className="h-8 w-8 rounded-md object-cover" src={product?.image} alt={product?.name} />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{product?.name}</div>
                            <div className="text-xs text-gray-500">{product?.product_code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {getWarehouseName(transaction.to_location_id)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <span className={effectColor}>
                          {transaction.movement_type === 'in' ? '+' : '-'}{transaction.quantity} {product?.unit_of_measure || '個'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        HK${(transaction.unit_cost || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        HK${(transaction.total_cost || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600 hover:text-blue-800">
                        {transaction.reference_id}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-full">
      {/* Enhanced Navigation Header */}
      {navigationSource === 'workflows' && (
        <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Activity className="w-5 h-5 text-purple-600" />
              <div>
                <h3 className="text-sm font-medium text-purple-800">
                  來自業務流程: 庫存管理與物料控制
                </h3>
                <p className="text-xs text-purple-600">
                  您正在執行生產週期的庫存管理步驟
                </p>
              </div>
            </div>
            <button
              onClick={handleReturnToWorkflows}
              className="flex items-center px-3 py-2 text-purple-700 hover:text-purple-800 hover:bg-purple-100 rounded-md transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              返回業務流程
            </button>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">庫存管理</h2>
        <p className="text-gray-600">管理產品庫存、倉庫和交易記錄</p>
      </div>
      
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'dashboard' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <div className="flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                <span>儀表板</span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'products' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <div className="flex items-center">
                <Package className="mr-2 h-5 w-5" />
                <span>產品</span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('warehouses')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'warehouses' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <div className="flex items-center">
                <Warehouse className="mr-2 h-5 w-5" />
                <span>倉庫</span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('transactions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'transactions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <div className="flex items-center">
                <Boxes className="mr-2 h-5 w-5" />
                <span>交易記錄</span>
              </div>
            </button>
          </nav>
        </div>
      </div>
      
      <div>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'products' && renderProducts()}
        {activeTab === 'warehouses' && renderWarehouses()}
        {activeTab === 'transactions' && renderTransactions()}
      </div>
      
      {/* Teaching Aids Panel */}
      {showTeachingAids && (
        <EnhancedTeachingAids 
          module="inventory" 
          isVisible={true}
        />
      )}
    </div>
  );
};

export default InventoryView;
